import { Component, Input, OnInit, OnChanges, OnDestroy, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environment';
import { SubGrupoDTO, SubGrupoResponseDTO } from '../../../models/subgrupo.model';
import { GrupoDTO } from '../../../models/grupo.model';
import { SubGrupoService } from '../../../services/grupo/sub-grupo.service';
import { SafeStorageService } from '../../safe-storage.service';

@Component({
  selector: 'app-select-subgrupo',
  templateUrl: './select-subgrupo.component.html',
  styleUrls: ['./select-subgrupo.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectSubgrupoComponent),
      multi: true
    }
  ]
})
export class SelectSubgrupoComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  /* ======================= *
   * Inputs
   * ======================= */
  @Input() placeholder: string = '';
  @Input() returnType: 'object' | 'id' = 'object';
  /** Quando informado, filtra subgrupos que pertencem ao grupo selecionado */
  @Input() grupoId: number | null | undefined = null;
  @Input() showAddButton: boolean = true;

  /* ======================= *
   * Estado interno
   * ======================= */
  env = environment;
  idEmpresa: string = '';
  subgrupos: SubGrupoDTO[] = [];
  /** Lista completa de subgrupos (sem filtro de grupo), usada para filtragem local */
  private allSubgrupos: SubGrupoDTO[] = [];
  loading = false;
  search = '';
  page = 0;
  size = 20;
  totalRecords = 0;
  hasMore = true; // Flag para indicar se há mais registros
  isLoadingMore = false; // Flag para carregamento incremental
  /** Indica se o dropdown deve ficar desabilitado (nenhum grupo selecionado) */
  disabled = false;

  /** valor que o p-dropdown usa */
  value: any = null;

  /** Visibilidade do dialog de nova subcategoria */
  dialogVisible: boolean = false;

  /** Texto do botao "+ Agregar Subcategoria" (carregado via i18n) */
  textoAgregarSubcategoria: string = '';

  private langSub?: Subscription;

  /* ======================= *
   * ControlValueAccessor
   * ======================= */
  onChange: (value: any) => void = () => { };
  onTouched: () => void = () => { };

  constructor(
    private safe: SafeStorageService,
    private subgrupoService: SubGrupoService,
    private translate: TranslateService
  ) { }

  /* ======================= *
   * Lifecycle
   * ======================= */
  ngOnInit(): void {
    this.idEmpresa = this.safe.getItem('id') || '';
    this.disabled = this.grupoId === null || this.grupoId === undefined;
    this.loadInitial(); // Carrega dados iniciais
    this.loadTextoAgregar();
    this.langSub = this.translate.onLangChange.subscribe(() => {
      this.loadTextoAgregar();
    });
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['grupoId'] && !changes['grupoId'].firstChange) {
      const novoGrupoId = changes['grupoId'].currentValue;
      const anteriorGrupoId = changes['grupoId'].previousValue;
      this.disabled = novoGrupoId === null || novoGrupoId === undefined;

      // Limpa seleção atual apenas quando o usuario troca de grupo
      // (nao limpa na carga inicial quando o valor anterior era null/undefined)
      const trocouDeGrupo = anteriorGrupoId !== null && anteriorGrupoId !== undefined;
      if (trocouDeGrupo && this.value !== null && this.value !== undefined) {
        this.value = null;
        this.onChange(null);
      }

      // Filtra subgrupos pelo novo grupo
      this.filtrarPorGrupo();
    }
  }

  /* ======================= *
   * ControlValueAccessor impl
   * ======================= */
  writeValue(value: any): void {
    this.value = value;

    // Se já recebeu valor (edição) e a lista já existe, sincroniza
    if (value && this.subgrupos.length) {
      this.syncSelectedValue();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  /* ======================= *
   * Seleção
   * ======================= */
  onSelect(value: any): void {
    this.value = value;
    this.onChange(value);
  }

  /* ======================= *
   * Carga inicial
   * ======================= */
  loadInitial(): void {
    this.loading = true;
    this.page = 0;
    this.hasMore = true;
    this.subgrupos = []; // Limpa lista existente
    this.allSubgrupos = [];

    this.subgrupoService.get('/subgrupo/filtrarSubGrupo', {
      page: this.page,
      size: this.size,
      sort: 'id,asc',
      filter: `&nome=${this.search}`
    }).subscribe({
      next: (res: SubGrupoResponseDTO) => {
        this.allSubgrupos = [...res.content];
        this.totalRecords = res.totalElements || 0;
        this.hasMore = this.allSubgrupos.length < this.totalRecords;
        this.filtrarPorGrupo();
        this.syncSelectedValue();
      },
      error: () => {
        // Subgrupos são opcionais — falha silenciosa
        this.subgrupos = [];
        this.allSubgrupos = [];
        this.hasMore = false;
        this.loading = false;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  /* ======================= *
   * Filtragem por grupo
   * ======================= */
  private filtrarPorGrupo(): void {
    if (this.grupoId === null || this.grupoId === undefined) {
      // Sem grupo selecionado: mostra todos (comportamento original)
      this.subgrupos = this.allSubgrupos.filter(
        (s, i, arr) => arr.findIndex(x => x.id === s.id) === i
      );
      return;
    }

    // Filtra subgrupos que pertencem ao grupo selecionado, removendo duplicatas por ID
    const filtrados = this.allSubgrupos.filter((subgrupo: SubGrupoDTO) => {
      if (subgrupo.grupos && Array.isArray(subgrupo.grupos)) {
        return subgrupo.grupos.some((g: GrupoDTO) => g.id === this.grupoId);
      }
      // Fallback: se tiver grupo_id direto (cast para any por seguranca)
      if ((subgrupo as any).grupo_id !== undefined) {
        return (subgrupo as any).grupo_id === this.grupoId;
      }
      return false;
    });
    this.subgrupos = filtrados.filter(
      (s, i, arr) => arr.findIndex(x => x.id === s.id) === i
    );
  }


  /* ======================= *
   * Filtro remoto
   * ======================= */
  onFilter(event: any): void {
    this.search = event.filter?.trim() || '';
    this.loadInitial(); // Reinicia busca com novo filtro
  }

  /* ======================= *
   * Sincronização edição
   * ======================= */
  private syncSelectedValue(): void {
    if (!this.value) return;

    // quando retorna id, garantir que o subgrupo exista na lista
    if (this.returnType === 'id') {
      const exists = this.subgrupos.some(g => g.id === this.value);
      if (!exists) {
        this.loadSubGrupoById(this.value);
      }
    }
  }

  private loadSubGrupoById(id: number): void {
    this.subgrupoService.getById(`/subgrupo/${id}`).subscribe({
      next: (subgrupo: SubGrupoDTO) => {
        if (!this.subgrupos.some(s => s.id === subgrupo.id)) {
          this.subgrupos = [subgrupo, ...this.subgrupos];
        }
      },
      error: (err) => {
        if (err?.status === 404) {
          console.warn(`SubGrupo com ID ${id} não encontrado.`);
          return;
        }
        console.error('Erro ao carregar subgrupo por ID:', err);
      }
    });
  }

  /* ======================= *
   * Dialog de nova subcategoria
   * ======================= */

  /**
   * Handler do click no footer "+ Agregar Subcategoria".
   * Usa (mousedown) com stopPropagation para impedir
   * que o dropdown feche antes de processar o clique.
   */
  onAddNew(event: MouseEvent): void {
    event.stopPropagation();
    this.dialogVisible = true;
  }

  /**
   * Handler chamado quando o dialog emite uma subcategoria criada.
   * Insere a subcategoria no inicio da lista e a seleciona automaticamente.
   */
  onSubgrupoCriado(subgrupo: SubGrupoDTO): void {
    // Inserir a subcategoria no inicio das listas
    this.allSubgrupos = [subgrupo, ...this.allSubgrupos];
    this.subgrupos = [subgrupo, ...this.subgrupos];
    this.totalRecords += 1;

    // Selecionar a subcategoria recem-criada
    if (this.returnType === 'id') {
      this.value = subgrupo.id;
    } else {
      this.value = subgrupo;
    }

    // Propagar a mudanca para o formulario pai
    this.onChange(this.value);
  }

  /* ======================= *
   * Util
   * ======================= */
  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src =
      'assets/images/profile_placeholder.png';
  }

  private loadTextoAgregar(): void {
    this.textoAgregarSubcategoria = this.translate.instant('select-subcategoria.botoes.agregar_subcategoria');
  }
}